/**
 * Smart Separator Web Component
 *
 * Automatically hides redundant separators when multiple separators are adjacent
 * (with empty or hidden blocks between them). Keeps the first separator visible
 * and hides subsequent ones to prevent visual stacking issues.
 *
 * Uses MutationObserver to dynamically respond to content changes.
 */
if (!customElements.get('smart-separator')) {
  customElements.define(
    'smart-separator',
    class SmartSeparator extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.initialized = false;
      }

      connectedCallback() {
        if (!this.isConnected) return;

        // Wait for DOM to be fully loaded and other scripts to run
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            setTimeout(() => this.initialize(), 100);
          });
        } else {
          setTimeout(() => this.initialize(), 100);
        }
      }

      disconnectedCallback() {
        this.cleanup();
      }

      initialize() {
        if (this.initialized) return;
        this.initialized = true;

        this.evaluateVisibility();
        this.setupObserver();
      }

      cleanup() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        this.initialized = false;
      }

      /**
       * Check if a block is effectively empty (hidden or has no visible content)
       */
      isBlockEffectivelyEmpty(block) {
        if (!block) return true;

        const blockType = block.getAttribute('data-block-type');

        // Check if block is hidden via CSS
        const computedStyle = window.getComputedStyle(block);
        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
          return true;
        }

        // Check if block has been explicitly hidden by script
        if (block.style.display === 'none') {
          return true;
        }

        // Special cases for specific block types
        switch (blockType) {
          case 'variant-picker':
            // Variant picker is empty if it only contains noscript
            const hasOnlyNoscript = block.children.length === 1 && block.querySelector('noscript');
            return hasOnlyNoscript;

          case 'tmr-variants':
            // TMR variants block is empty if it has no variant content
            const hasVariantContent = block.querySelector('#color-variants, #box-variants');
            if (!hasVariantContent) return true;
            break;

          case 'scent-selector':
            // Scent selector might be hidden based on tags
            const hasScentContent = block.querySelector('.scent-selector__options');
            if (!hasScentContent) return true;
            break;
        }

        // Check if block has only whitespace text content AND no visible elements
        const textContent = block.textContent.trim();
        if (textContent === '') {
          const hasVisibleElements = Array.from(block.children).some((child) => {
            const childStyle = window.getComputedStyle(child);
            return (
              childStyle.display !== 'none' &&
              childStyle.visibility !== 'hidden' &&
              child.tagName !== 'SCRIPT' &&
              child.tagName !== 'NOSCRIPT' &&
              child.tagName !== 'STYLE'
            );
          });
          return !hasVisibleElements;
        }

        // All other blocks with content are considered non-empty
        return false;
      }

      /**
       * Find the nearest non-empty block in a given direction
       */
      findNearestNonEmptyBlock(startBlock, direction = 'next') {
        let currentBlock = direction === 'next' ? startBlock.nextElementSibling : startBlock.previousElementSibling;

        while (currentBlock) {
          if (!this.isBlockEffectivelyEmpty(currentBlock)) {
            return currentBlock;
          }
          currentBlock = direction === 'next' ? currentBlock.nextElementSibling : currentBlock.previousElementSibling;
        }
        return null;
      }

      /**
       * Evaluate whether this separator should be visible
       */
      evaluateVisibility() {
        // Find the parent block item wrapper
        const parentBlock = this.closest('.product-info__block-item');
        if (!parentBlock) {
          // If no parent block, show the separator
          this.style.display = '';
          return;
        }

        // Find the previous non-empty block before this separator's parent block
        const previousNonEmptyBlock = this.findNearestNonEmptyBlock(parentBlock, 'previous');

        // Hide this separator if the previous non-empty block is also a separator
        // This handles the case: Separator → Empty blocks → Separator
        // We keep the first separator and hide the second one
        if (previousNonEmptyBlock && previousNonEmptyBlock.getAttribute('data-block-type') === 'separator') {
          this.style.display = 'none';
          return;
        }

        // Show the separator if the previous block is not a separator
        this.style.display = '';
      }

      /**
       * Setup MutationObserver to watch for dynamic content changes
       */
      setupObserver() {
        if (!window.MutationObserver) return;

        // Observe the parent container for changes
        const parentContainer = this.closest('.product-info__block-list');
        if (!parentContainer) return;

        this.observer = new MutationObserver(() => {
          // Debounce the re-evaluation
          if (this.evaluationTimeout) {
            clearTimeout(this.evaluationTimeout);
          }
          this.evaluationTimeout = setTimeout(() => {
            this.evaluateVisibility();
          }, 50);
        });

        this.observer.observe(parentContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'hidden', 'class'],
        });
      }
    }
  );
}

